import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Wind, Sun, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const WindPower = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/wind-power"]?.title} description={seoData["industries/wind-power"]?.description} path="/industries/wind-power" keywords={seoData["industries/wind-power"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Wind & Renewable Energy"
        description="Coating and repair for wind turbines and renewable energy infrastructure. Leading edge protection, tower coating, foundation repair, and corrosion prevention for wind farms."
        backgroundKey="wind-power"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Leading edge erosion on turbine blades from rain, sand, and insects',
              'Tower corrosion from weathering and coastal salt spray',
              'Foundation cracking and concrete deterioration',
              'Bolt and flange corrosion at tower sections',
              'Nacelle and hub component wear',
              'Substation equipment corrosion'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-sky-50 rounded-lg">
                <Wind className="text-sky-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-sky-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Sun className="text-sky-600 mr-3 flex-shrink-0" size={22} />
                  Leading Edge Protection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Erosion-resistant coating systems for wind turbine blade leading edges. Polyurethane and elastomeric systems that withstand rain, sand, and particle impact at tip speeds exceeding 200 mph.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-sky-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Sun className="text-sky-600 mr-3 flex-shrink-0" size={22} />
                  Tower Coating & Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Full tower recoating and spot repair for corrosion, coating failure, and weathering damage. Multi-coat systems for long-term atmospheric corrosion protection.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-sky-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Sun className="text-sky-600 mr-3 flex-shrink-0" size={22} />
                  Foundation Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Concrete foundation crack repair, spall repair, and protective coating for wind turbine bases. Epoxy injection and structural repair for foundation integrity.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-sky-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Sun className="text-sky-600 mr-3 flex-shrink-0" size={22} />
                  Bolted Connection Protection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Corrosion protection and lubricant systems for tower section bolted flanges, blade root connections, and yaw bearing assemblies.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Wind & Renewable Energy?</h2>
              <div className="space-y-3">
                {['Rope access and at-height capabilities for tower and blade work',
                  'Turbine-specific coating systems for leading edge and tower protection',
                  'Minimize turbine downtime with efficient repair scheduling',
                  'Wind farm-wide coating programs for fleet maintenance',
                  'Corrosion protection for coastal and high-humidity environments',
                  'Full QA/QC documentation for OEM warranty compliance'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-sky-600 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Wind Farm Coating Programs</h3>
              <p className="text-sky-100 mb-6">BCS provides fleet-wide coating maintenance programs for wind farm operators. Systematic inspection and repair to maximize turbine availability and reduce O&M costs.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/structural-repair')}>
                View Structural Repair <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Wind Farm Assets</h2>
          <p className="text-blue-100 mb-8">Contact us for a wind turbine coating assessment and fleet maintenance proposal.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WindPower;
